<script setup lang="ts">
    import EchoButton from '@/js/components/EchoButton.vue';
    import IconButton from '@/js/components/IconButton.vue';
    import { ResponseColumnHeader } from '@/js/services/api/interaction/types';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { computed, ref, watch } from 'vue';
    import {
        InteractionTableStoreName,
        useInteractionTableStoreByName,
    } from '@/js/stores/interactions-table';
    import Modal, { ModalSize } from '../Modal.vue';
    import { PlusIcon, XIcon } from '@lucide/vue';

    const props = defineProps<{
        showColumnModal: boolean;
        storeName: InteractionTableStoreName;
    }>();

    const emit = defineEmits<{
        (e: 'close'): void;
        (e: 'submit'): void;
    }>();

    const columns = ref<Record<string, ResponseColumnHeader[]>>({
        'categories': [],
        'sentiment-data': [],
        'metadata': [],
        'other': [],
    });

    const visibleColumns = computed(() =>
        columns.value.categories
            .concat(columns.value['sentiment-data'])
            .concat(columns.value.metadata)
            .concat(columns.value.other)
            .filter((col) => col.order !== null)
    );

    const interactionTableStore = useInteractionTableStoreByName(props.storeName);

    const getCategoryFromKey = (key: string) => {
        switch (key) {
            case 'Category':
                return 'categories';
            case 'Skill':
                return 'metadata';
            case 'Tracker':
                return 'metadata';
            case 'SentimentCustomerDirection':
                return 'sentiment-data';
            case 'SentimentAgentDirection':
                return 'sentiment-data';
            case 'Queue':
                return 'metadata';
            default:
                return 'other';
        }
    };

    const setInitialColumns = () => {
        interactionTableStore.tableColumns?.forEach((col) => {
            if ((col.translationKey as string) === 'table-interaction-column-main-category') return;
            if (col.expandsFromParentId !== null) return;
            if (col.type === 'ProtocolResults') return;
            columns.value[getCategoryFromKey(col.filterKey)].push({ ...col });
        });
    };

    const hideAllColumns = () => {
        visibleColumns.value.forEach((col) => {
            col.order = null;
        });
    };

    watch(
        () => props.showColumnModal,
        (newVal) => {
            if (newVal) {
                setInitialColumns();
            } else {
                columns.value = {
                    'categories': [],
                    'sentiment-data': [],
                    'metadata': [],
                    'other': [],
                };
            }
        }
    );

    const addColumn = (column: ResponseColumnHeader) => {
        if (column.order === null) {
            const maxOrder = Math.max(
                0,
                ...(visibleColumns.value.map((col) => col.order || 0) || [])
            );
            column.order = maxOrder + 1;
        }
    };

    const submit = () => {
        const updatedColumns: ResponseColumnHeader[] = columns.value.categories
            .concat(columns.value['sentiment-data'])
            .concat(columns.value.metadata)
            .concat(columns.value.other);

        // Re-inject main-category (hidden, merged into sub-category column) so the backend gets a complete column list
        const mainCategory = interactionTableStore.tableColumns?.find(
            (c) => (c.translationKey as string) === 'table-interaction-column-main-category'
        );
        if (mainCategory) {
            updatedColumns.push({ ...mainCategory, order: null });
        }

        // Re-inject child columns excluded from the modal UI so expandedColumns stays valid
        const childColumns =
            interactionTableStore.tableColumns?.filter((c) => c.expandsFromParentId !== null) ?? [];
        updatedColumns.push(...childColumns.map((c) => ({ ...c })));

        // Re-inject ProtocolResults parent columns excluded from the modal UI (it only
        // lets users toggle base/metadata columns, not the protocol-results ones)
        const protocolResultsColumns =
            interactionTableStore.tableColumns?.filter(
                (c) => c.type === 'ProtocolResults' && c.expandsFromParentId === null
            ) ?? [];
        updatedColumns.push(...protocolResultsColumns.map((c) => ({ ...c })));

        interactionTableStore.setTableColumns(updatedColumns);
        emit('submit');
        emit('close');
    };

    const getColumnName = (column: ResponseColumnHeader) => {
        if (column.dynamicFilterKeyName) {
            return column.dynamicFilterKeyName;
        }
        return __untypedExpression(column.translationKey);
    };
</script>
<template>
    <Modal :show="showColumnModal" :size="ModalSize.Large" @close="$emit('close')">
        <h3 class="text-xl md:text-[28px] font-medium mb-5">
            {{ __('show-hide-column-modal-title') }}
        </h3>
        <div class="flex gap-1 h-full mb-2">
            <div class="grid grid-cols-2 gap-10 flex-1 pb-2">
                <div v-for="key in Object.keys(columns)" :key="`column-category-${key}`">
                    <h5 class="font-semibold mb-2">
                        {{ __untypedExpression(`interaction-table-column-category-${key}`) }}
                    </h5>
                    <template
                        v-for="column in columns[key]"
                        :key="`${key}-column-${column.filterKey}`"
                    >
                        <div class="flex gap-1.5 justify-between items-center mb-2">
                            <template v-if="column.order !== null">
                                <p class="text-body text-gray-light">
                                    {{ getColumnName(column) }}
                                </p>
                            </template>
                            <template v-else>
                                <p class="text-body">
                                    {{ getColumnName(column) }}
                                </p>
                                <div class="-m-1.5">
                                    <IconButton
                                        aria-label="add column"
                                        area-size="size-8"
                                        size="size-5"
                                        @click="addColumn(column)"
                                    >
                                        <PlusIcon class="size-5" />
                                    </IconButton>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>
            </div>
            <div class="w-[1px] py-2">
                <div class="h-full bg-gray-light w-[1px]" />
            </div>
            <div class="px-2 pb-2 w-[250px]">
                <h5 class="font-semibold mb-2">
                    {{ __('show-hide-column-modal-chosen-filters') }}
                </h5>

                <div class="flex gap-2 flex-wrap">
                    <template v-for="column in visibleColumns" :key="column.id">
                        <EchoButton size="small" variant="secondary" @click="column.order = null">
                            {{ getColumnName(column) }}
                            <template #icon-right><XIcon /></template>
                        </EchoButton>
                    </template>
                    <EchoButton size="small" variant="secondary" @click="hideAllColumns">
                        {{ __('reset-filters') }}
                        <template #icon-right><XIcon /></template>
                    </EchoButton>
                </div>
            </div>
        </div>
        <div class="flex justify-end mt-3">
            <EchoButton @click="submit">
                {{ __('done') }}
            </EchoButton>
        </div>
    </Modal>
</template>
